'use client';

import { Suspense, useState } from 'react';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import '@/lib/firebase-client';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Link from 'next/link';
import { AlertCircle } from 'lucide-react';
import { useUser } from '@/context/AuthContext';
import { useRegistration } from '@/components/RegistrationContext';
import { saveOnboardingState } from '@/services/onboarding-state';
import StepTransition from '@/components/onboarding/StepTransition';
import GlassCard from '@/components/onboarding/GlassCard';
import GlowInput from '@/components/onboarding/GlowInput';
import FaviconPreview from '@/components/onboarding/FaviconPreview';
import ScanAnimation from '@/components/onboarding/ScanAnimation';

const isValidUrl = (url) => {
  try {
    const u = url.startsWith('http') ? url : `https://${url}`;
    new URL(u);
    return true;
  } catch {
    return false;
  }
};

const schema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  password: yup.string().min(8, 'Must be at least 8 characters').required('Password is required'),
  website: yup.string().required('Website URL is required').test('is-valid-url', 'Please enter a valid URL', isValidUrl),
}).required();

function IdentityContent() {
  const { setRegistrationInProgress } = useRegistration();
  const router = useRouter();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanDomain, setScanDomain] = useState('');
  const [scanResult, setScanResult] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '', website: '' },
  });

  const websiteValue = watch('website');

  const onSubmit = async (formData) => {
    setRegistrationInProgress(true);
    setSubmitting(true);
    setError(null);

    try {
      const auth = getAuth();
      const cred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const token = await cred.user.getIdToken();

      // Set auth cookie so middleware allows /org/* routes later
      await fetch('/api/login', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      const websiteUrl = formData.website.startsWith('http')
        ? formData.website
        : `https://${formData.website}`;

      const response = await fetch('/api/register-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ email: formData.email, website: websiteUrl }),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Registration failed (server returned ${response.status})`);
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Registration failed');

      saveOnboardingState({
        orgId: result.orgId,
        websiteId: result.websiteId,
        engineId: result.engineId,
        discoveryUrlId: result.discoveryUrlId,
        email: formData.email,
        website: websiteUrl,
        step: 1,
        planFeatures: result.planFeatures,
        completedSteps: ['identity'],
        peersAdded: 0,
        pagesMonitored: 0,
        signalsConfigured: false,
      });

      setRegistrationInProgress(false);

      // Show scan animation
      try {
        setScanDomain(new URL(websiteUrl).hostname);
      } catch {
        setScanDomain(websiteUrl);
      }
      setScanResult(result);
      setScanning(true);

      if (typeof gtag !== 'undefined') {
        gtag('event', 'conversion', { send_to: 'AW-783295803/iAN4CJvq7eQZELvKwPUC' });
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
      setRegistrationInProgress(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScanComplete = () => {
    router.push('/onboarding/landscape');
  };

  if (scanning) {
    return (
      <StepTransition>
        <GlassCard>
          <ScanAnimation domain={scanDomain} onComplete={handleScanComplete} />
        </GlassCard>
      </StepTransition>
    );
  }

  return (
    <StepTransition>
      <div className="space-y-6">
        {/* Heading */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Enter your company URL to begin
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            We'll scan your site and set up your intelligence hub automatically.
          </p>
        </div>

        <GlassCard>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Website URL — primary input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="website" className="text-sm font-medium text-foreground">
                  Website
                </label>
                <FaviconPreview url={websiteValue} />
              </div>
              <GlowInput
                id="website"
                type="text"
                placeholder="https://www.yourcompany.com"
                autoComplete="url"
                error={!!errors.website}
                {...register('website')}
              />
              {errors.website && (
                <p className="text-sm text-destructive">{errors.website.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/40"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Create a password"
                autoComplete="new-password"
                className="w-full rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/40"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating account...' : 'Begin Scan →'}
            </button>

            <p className="text-center text-xs text-muted-foreground">
              By continuing, you agree to Tivion's{' '}
              <a href="/tos" className="text-primary hover:text-primary/80">Terms</a>{' '}
              and{' '}
              <a href="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</a>.
              No credit card required.
            </p>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="font-medium text-primary hover:text-primary/80">
                Sign in
              </Link>
            </p>
          </form>
        </GlassCard>
      </div>
    </StepTransition>
  );
}

export default function IdentityPage() {
  return (
    <Suspense fallback={<div className="flex min-h-[400px] items-center justify-center text-muted-foreground">Loading...</div>}>
      <IdentityContent />
    </Suspense>
  );
}
